<template>
  <div
    class="drop-area"
    @dragover.prevent
    @dragenter.prevent
    @drop.prevent="handleDrop"
    @paste="handlePaste"
  >
    <div class="d-flex flex-column gap-2 position-relative">

      <!-- Reply Info (Optional) -->
      <div
        id="chatInfo"
        class="alert alert-info d-flex justify-content-between align-items-center p-2 mb-0"
        v-if="$root.chatInfoText"
      >
        <span>
          <strong>You are replying to:</strong> {{ $root.chatInfoText }}
        </span>
        <button
          type="button"
          class="btn btn-sm btn-light"
          @click="$root.chatInfoText = null; $root.isReplying = false"
        >
          ×
        </button>
      </div>

      <!-- Chat Input Area -->
      <div class="d-flex align-items-end">
        <textarea
          id="chatInput"
          v-model="newMessage"
          class="form-control message-input"
          rows="1"
          ref="chatTextarea"
          @keydown.enter="handleEnter"
          @keydown.shift.enter.exact.stop
          placeholder="Type your message..."
          @input.stop="autoResize"
          @keydown.stop="handleKeyDown"
        ></textarea>

        <!-- Action Buttons (fixed height) -->
        <div class="btn-group align-self-end ms-2">
          <button class="btn btn-secondary btn-sm" @click="toggleEmojiPicker">😊</button>
          <button class="btn btn-secondary btn-sm" @click="triggerFileInput">📎</button>
          <button class="btn btn-primary btn-sm" id="msgSendBtn" @click="sendMessage">Send</button>
        </div>
      </div>

      <!-- Mention List Dropdown -->
      <div v-if="showMentionList" class="mention-list">
        <ul class="list-group">
          <li 
            v-for="(user, index) in filteredMentionUsers"
            :key="user.id"
            class="list-group-item list-group-item-action"
            :class="{ 'active': activeMentionIndex === index }"
            @click="selectMention(user)"
            @mouseover="activeMentionIndex = index"
          >
            {{ user.name }}
          </li>
          <li v-if="filteredMentionUsers.length === 0" class="list-group-item text-muted">
            No users found
          </li>
        </ul>
      </div>

      <!-- Hidden File Input -->
      <input
        type="file"
        ref="fileInput"
        @change="handleFileChange"
        style="display: none;"
        multiple
      />

      <!-- Emoji Picker -->
      <div v-if="showEmojiPicker" class="emoji-picker-container">
        <Picker set="apple" @select="addEmoji" />
      </div>
    </div>

    <!-- Upload Progress Bar -->
    <div v-if="isUploading" class="upload-progress mt-2">
      <div class="d-flex justify-content-between align-items-center mb-1">
        <small class="text-muted">Uploading files...</small>
        <small class="text-muted">{{ uploadProgress }}%</small>
      </div>
      <div class="progress">
        <div 
          class="progress-bar progress-bar-striped progress-bar-animated" 
          role="progressbar" 
          :style="{ width: uploadProgress + '%' }"
          :aria-valuenow="uploadProgress"
          aria-valuemin="0" 
          aria-valuemax="100"
        ></div>
      </div>
    </div>

    <!-- File Preview -->
    <ul class="list-group mt-2" v-if="files.length">
      <li
        v-for="(file, index) in files"
        :key="index"
        class="d-flex justify-content-between align-items-center"
      >
        <div>
          <span v-if="file.type.startsWith('image/')">
            <img
              :src="getFilePreview(file)"
              alt="preview"
              width="50"
              class="me-2"
            />
          </span>
          {{ file.name }}
        </div>
        <button
          type="button"
          class="btn btn-sm btn-outline-danger ms-2"
          @click="removeFile(index)"
        >
          ×
        </button>
      </li>
    </ul>
  </div>
</template>

<script>
import { Picker } from 'emoji-mart-vue';
import 'emoji-mart-vue/css/emoji-mart.css';
import axios from 'axios';
import { EventBus } from './../eventBus';

export default {
  components: { Picker },
  props: ["user", "chanel"],
  data() {
    return {
      newMessage: "",
      files: [],
      showEmojiPicker: false,
      isEdit: false,
      
      // Upload progress tracking
      isUploading: false,
      uploadProgress: 0,
      
      // Mention feature data
      mentionUsers: [],
      showMentionList: false,
      mentionQuery: '',
      mentionStartPos: 0,
      activeMentionIndex: 0,
      mentionDebounce: null,
    };
  },
  computed: {
    filteredMentionUsers() {
      if (!this.mentionQuery) return this.mentionUsers;
      return this.mentionUsers.filter(user => 
        user.name.toLowerCase().includes(this.mentionQuery.toLowerCase())
      );
    }
  },
  methods: {
    handleEnter(event) {
      if (!event.shiftKey) {
        event.preventDefault();
        this.sendMessage();
      }
    },
    
    handleKeyDown(e) {
      // Handle mention list navigation
      if (this.showMentionList) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          this.navigateMentions(1);
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          this.navigateMentions(-1);
        } else if (e.key === 'Enter') {
          e.preventDefault();
          this.selectMentionWithKeyboard();
        } else if (e.key === 'Escape') {
          this.showMentionList = false;
        }
      }
      
      // Check for mentions
      this.checkForMention(e);
    },
    
     autoResize(e) {
      const ta = e.target;
      if (this._rafId) return;            // coalesce events in same frame
      this._rafId = requestAnimationFrame(() => {
        this._rafId = null;
        ta.style.height = 'auto';
        const next = ta.scrollHeight;
        if (next !== this._lastHeight) {
          ta.style.height = next + 'px';
          this._lastHeight = next;
        }
      });
    },
    
    getFilePreview(file) {
      return window.URL.createObjectURL(file);
    },
    
    handlePaste(event) {
      const items = (event.clipboardData || event.originalEvent.clipboardData).items;
      for (let item of items) {
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file && file.type.startsWith('image/')) {
            this.files.push(file);
          }
        }
      }
    },
    
    removeFile(index) {
      this.files.splice(index, 1);
    },
    
    toggleEmojiPicker() {
      this.showEmojiPicker = !this.showEmojiPicker;
      this.showMentionList = false;
    },
    
    addEmoji(emoji) {
      this.newMessage += emoji.native;
      this.showEmojiPicker = false;
      this.$nextTick(() => {
        this.$refs.chatTextarea.focus();
      });
    },
    
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    
    handleFileChange(e) {
      this.files = Array.from(e.target.files);
    },
    
    handleDrop(event) {
      const droppedFiles = Array.from(event.dataTransfer.files);
      this.files.push(...droppedFiles);
    },
    
    // Mention feature methods
    checkForMention(e) {
      if (this.showEmojiPicker) return;

      const text = this.newMessage;
      const cursorPos = e.target.selectionStart;
      const before = text.substring(0, cursorPos);
      const lastAt = before.lastIndexOf('@');

      const looksLikeMention = lastAt >= 0 && (lastAt === 0 || /\s/.test(before[lastAt - 1]));
      if (!looksLikeMention) {
        this.showMentionList = false;
        return;
      }

      const q = before.substring(lastAt + 1).toLowerCase();

      // Tiny debounce (60–100ms) so we’re not filtering on every keystroke
      clearTimeout(this.mentionDebounce);
      this.mentionDebounce = setTimeout(() => {
        this.showMentionList = q.length >= 1;     // avoid showing on bare "@"
        this.mentionStartPos = lastAt + 1;
        this.mentionQuery = q;
        this.activeMentionIndex = 0;
      }, 80);
    },
    
    selectMention(user) {
      const textBefore = this.newMessage.substring(0, this.mentionStartPos - 1);
      const textAfter = this.newMessage.substring(
        this.mentionStartPos - 1 + this.mentionQuery.length + 1
      );

      // Just insert plain text @mention into textarea
      this.newMessage = `${textBefore}@${user.name} ${textAfter}`;

      this.showMentionList = false;
      this.mentionQuery = '';

      this.$nextTick(() => {
        this.$refs.chatTextarea.focus();
        const newCursorPos = (textBefore + '@' + user.name + ' ').length;
        this.$refs.chatTextarea.setSelectionRange(newCursorPos, newCursorPos);
      });
    },
    
    navigateMentions(direction) {
      const userCount = this.filteredMentionUsers.length;
      if (userCount === 0) return;
      
      this.activeMentionIndex = (this.activeMentionIndex + direction + userCount) % userCount;
      
      // Scroll into view
      this.$nextTick(() => {
        const activeElement = this.$el.querySelector('.mention-list li.active');
        if (activeElement) {
          activeElement.scrollIntoView({ block: 'nearest' });
        }
      });
    },
    
    selectMentionWithKeyboard() {
      if (this.filteredMentionUsers.length === 0) return;
      const user = this.filteredMentionUsers[this.activeMentionIndex];
      this.selectMention(user);
    },
    
    async sendMessage() {
      const sendButton = this.$el.querySelector('#msgSendBtn');
      sendButton.disabled = true;

      if (!this.newMessage.trim() && this.files.length === 0) {
        alert("Please enter a message or select files.");
        sendButton.disabled = false;
        return;
      }

      const formData = new FormData();
      
      // Process mentions before sending (convert to special format if needed)
      let processedMessage = this.newMessage;
      // You can add additional processing here if needed
      this.mentionUsers.forEach(user => {
        const regex = new RegExp(`@${user.name}\\b`, 'g');
        processedMessage = processedMessage.replace(
          regex,
          `<a href="#user-${user.id}" class="mention" data-user-id="${user.id}">@${user.name}</a>`
        );
      });
      
      formData.append('message', processedMessage);
      formData.append('chanel', this.chanel);
      this.files.forEach(file => formData.append('files[]', file));
      
      if (this.$root.isEditing && this.$root.messageId) {
        formData.append('edit', true);
        formData.append('message_id', this.$root.messageId);
      } else if (this.$root.isReplying && this.$root.messageId) {
        formData.append('reply', true);
        formData.append('message_id', this.$root.messageId);
      } else {
        this.$root.chatInfoText = null;
        this.$root.isReplying = false;  
      }

      try {
        // Set upload state if files are being uploaded
        if (this.files.length > 0) {
          this.isUploading = true;
          this.uploadProgress = 0;
        }

        const response = await axios.post('/account/private-chat/messages', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            if (this.files.length > 0) {
              this.uploadProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            }
          }
        });

        if (response.status !== 200) {
          throw new Error("Failed to send message");
        }
        
        this.$toast && this.$toast.success("Message sent!");

        if (this.$root.isReplying) {
          $('.chat-item[data-channel="'+this.chanel+'"]').click();
          this.$root.chatInfoText = '';
          this.$root.isReplying = false;
        }

        if (this.$root.isEditing) {
          const messageElement = document.querySelector(`.chat-message[data-id="${this.$root.messageId}"]`);
          if (messageElement) {
            messageElement.querySelector('p.chat-message-content').innerHTML = response.data.message;
            this.$root.messageId = null;
          }
          const chatMessageElement = messageElement.closest('.chat-message');
          if (chatMessageElement) {
            const messageStatusElement = chatMessageElement.querySelector('.message-status');
            if (messageStatusElement) {
              messageStatusElement.textContent = 'Edited';
            }
          }
          
          this.$root.isEditing = false;
          this.$root.messageId = null;
          this.newMessage = "";
          this.files = [];
          this.$refs.fileInput.value = null;
          this.showEmojiPicker = false;
          sendButton.disabled = false;
          return;
        }

        if (this.$root.isReplying && this.$root.messageId) {
          this.$root.isReplying = false;
          this.$root.messageId = null;
          $('.chat-chanel-link .active').click();
          this.newMessage = "";
        }

        EventBus.$emit('custom-event', {
          user: this.user,
          message: response.data.message,
          chanel: this.chanel
        });

        if (typeof appendMessageToChatBox === 'function') {
          appendMessageToChatBox(response.data);
          $('#chat-content').scrollTop($('#chat-content')[0].scrollHeight);
          chatMessages.push(response.data);
        }
        
        this.newMessage = "";
        this.files = [];
        this.$refs.fileInput.value = null;
        this.showEmojiPicker = false;
        this.showMentionList = false;
        
        // Reset upload progress state
        this.isUploading = false;
        this.uploadProgress = 0;
        
        sendButton.disabled = false;
        
        this.$nextTick(() => {
          const textarea = this.$refs.chatTextarea;
          this.autoResize({ target: textarea });
        });

        // Clear chat info text if it was set
        if (this.$root.chatInfoText) {
          this.$root.chatInfoText = null;
          this.$root.isReplying = false;
        }
      } catch (error) {
        if (error.response && error.response.status === 422) {
          const errors = error.response.data.errors;
          const errorMessages = Object.values(errors).flat().join('<br>');
          alert(errorMessages);
        } else {
          console.error("Unexpected error:", error);
          alert("Unexpected error");
        }
        
        // Reset upload progress state on error
        this.isUploading = false;
        this.uploadProgress = 0;
        
        sendButton.disabled = false;
      }
    }
  },
  mounted() {
    window.chatFormComponent = this;
    this.$nextTick(() => {
      if (this.$refs.chatTextarea) {
        this.$refs.chatTextarea.style.height = 'auto';
        this.$refs.chatTextarea.style.height = this.$refs.chatTextarea.scrollHeight + 'px';
      }
    });
  }
};
</script>

<style scoped>
.drop-area {
  border: 2px dashed #ccc;
  border-radius: 10px;
  padding: 10px;
  transition: background-color 0.3s;
}

.drop-area:hover {
  background-color: #f8f9fa;
  cursor: pointer;
}

.message-input {
  resize: none;
  overflow: hidden;
  min-height: 38px;
  max-height: 150px;
  line-height: 1.5;
  padding: 6px 12px;
}

.d-flex.align-items-end {
  align-items: stretch !important;
  gap: 0.5rem;
}

.btn-group.ms-2 {
  display: flex;
  align-items: center;
}

.btn-group .btn {
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.emoji-picker-container {
  position: absolute;
  bottom: 45px;
  right: 80px;
  z-index: 9999;
  background-color: white;
  border: 1px solid #ddd;
  border-radius: 8px;
}

/* Mention list styles */
.mention-list {
  position: absolute;
  bottom: 100%;
  left: 0;
  width: 200px;
  max-height: 150px;
  overflow-y: auto;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  z-index: 1000;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.mention-list ul {
  margin: 0;
  padding: 0;
  list-style: none;
}

.mention-list li {
  padding: 6px 12px;
  cursor: pointer;
}

.mention-list li:hover,
.mention-list li.active {
  background-color: #084298;
}

.mention-list li:first-child {
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
}

.mention-list li:last-child {
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
}

/* Mention styling in messages */
.mention {
  color: #4a89dc;
  font-weight: bold;
  text-decoration: none;
}

.mention:hover {
  text-decoration: underline;
}
</style>